import type { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Box, CircularProgress, Chip, Paper, Typography } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { Skill, SkillCategory } from '@/types/domain';
import { CATEGORY_LABELS, CATEGORY_COLORS } from '../constants';
import SkillMoreActions from './SkillMoreActions';

export interface SkillTableProps {
  skills: Skill[];
  loading: boolean;
  onEdit: (skill: Skill) => void;
  onDelete: (skill: Skill) => void;
}

/**
 * 技能列表表格组件
 * 展示技能数据并提供编辑和删除操作
 */
export default function SkillTable({ skills, loading, onEdit, onDelete }: SkillTableProps) {
  const { t } = useTranslation();

  const columns: GridColDef<Skill>[] = [
    {
      field: 'id',
      headerName: t('skills.id'),
      width: 160,
      renderCell: (params: GridRenderCellParams<Skill>) => (
        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'name',
      headerName: t('skills.name'),
      width: 220,
      renderCell: (params: GridRenderCellParams<Skill>) => {
        const skill = params.row;
        if (!skill) {
          return null;
        }

        return (
          <Box>
            <Typography variant="body2" fontWeight={skill.builtin ? 600 : 400}>
              {params.value}
              {skill.builtin && (
                <Chip
                  label={t('skills.builtin')}
                  size="small"
                  sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                  color="info"
                />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {skill.description?.substring(0, 40)}
              {skill.description?.length > 40 ? '...' : ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'category',
      headerName: t('skills.category'),
      width: 160,
      renderCell: (params: GridRenderCellParams<Skill, SkillCategory>) => {
        const category = params.value;
        if (!category) {
          return null;
        }
        return (
          <Chip
            label={CATEGORY_LABELS[category] || category}
            size="small"
            color={CATEGORY_COLORS[category] || 'default'}
          />
        );
      },
    },
    {
      field: 'priority',
      headerName: t('skills.priority'),
      width: 110,
      renderCell: (params: GridRenderCellParams<Skill>) => (
        <Typography variant="body2">{params.value}</Typography>
      ),
    },
    {
      field: 'example',
      headerName: 'Example',
      width: 220,
      renderCell: (params: GridRenderCellParams<Skill>) => {
        const exampleText = params.row?.example;
        if (!exampleText) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }

        const preview =
          exampleText.length > 100
            ? `${exampleText.substring(0, 100)}...`
            : exampleText;

        return (
          <Typography
            variant="body2"
            sx={{ fontSize: '0.75rem', maxWidth: 220, wordBreak: 'break-word' }}
          >
            {preview}
          </Typography>
        );
      },
    },
    {
      field: 'version',
      headerName: t('skills.version'),
      width: 90,
      renderCell: (params: GridRenderCellParams<Skill>) => (
        <Chip label={`v${params.value}`} size="small" variant="outlined" />
      ),
    },
    {
      field: 'created_at',
      headerName: t('skills.createdAt'),
      width: 180,
      renderCell: (params: GridRenderCellParams<Skill>) => {
        try {
          const value = params.row?.created_at;
          if (!value) {
            return '-';
          }
          const date = new Date(value.includes(' ') ? value.replace(' ', 'T') : value);
          if (Number.isNaN(date.getTime())) {
            return '-';
          }
          return format(date, 'yyyy-MM-dd HH:mm');
        } catch {
          return '-';
        }
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('skills.actions'),
      width: 90,
      getActions: (params: GridRowParams<Skill>) => {
        const skill = params.row;
        if (!skill) {
          return [];
        }

        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label={t('common.edit')}
            onClick={() => onEdit(skill)}
            disabled={!!skill.builtin}
            showInMenu={false}
          />,
        ];
      },
    },
    {
      field: 'more',
      type: 'actions',
      headerName: '',
      width: 50,
      getActions: (params: GridRowParams<Skill>) => {
        const skill = params.row;
        if (!skill) {
          return [];
        }

        return [
          <SkillMoreActions
            key="more"
            skill={skill}
            onEdit={() => onEdit(skill)}
            onDelete={() => onDelete(skill)}
          />,
        ];
      },
    },
  ];

  if (loading) {
    return (
      <Paper>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper>
      <DataGrid
        rows={skills}
        columns={columns}
        getRowId={(row) => row.id}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
          sorting: {
            sortModel: [{ field: 'priority', sort: 'desc' }],
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
          },
        }}
      />
    </Paper>
  );
}
